import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Grid, Card, CardContent, Chip,
  Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, LinearProgress, TextField, MenuItem
} from '@mui/material';
import { School, People, EmojiEvents, MenuBook } from '@mui/icons-material';
import axios from 'axios';

const api = {
  get: (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug') }}),
};

const gpaColor = (gpa) => {
  const g = parseFloat(gpa);
  if (g >= 3.7) return '#7b1fa2';
  if (g >= 3.3) return '#1565c0';
  if (g >= 3.0) return '#2e7d32';
  if (g >= 2.0) return '#c9952a';
  return '#c62828';
};

const getClassification = (gpa) => {
  const g = parseFloat(gpa);
  if (g >= 3.7) return 'Summa Cum Laude';
  if (g >= 3.3) return 'Cum Laude';
  if (g >= 2.5) return 'Distinction';
  if (g >= 2.0) return 'Merit';
  if (g > 0)   return 'Pass';
  return 'No grades';
};

const GPA_BANDS = [
  { label:'3.7 – 4.0', desc:'Summa Cum Laude', color:'#7b1fa2', min:3.7, max:4.0 },
  { label:'3.3 – 3.69', desc:'Cum Laude',      color:'#1565c0', min:3.3, max:3.69 },
  { label:'3.0 – 3.29', desc:'Distinction',    color:'#2e7d32', min:3.0, max:3.29 },
  { label:'2.5 – 2.99', desc:'Merit',          color:'#c9952a', min:2.5, max:2.99 },
  { label:'2.0 – 2.49', desc:'Pass',           color:'#546e7a', min:2.0, max:2.49 },
  { label:'0 – 1.99',   desc:'Below Pass',     color:'#c62828', min:0,   max:1.99 },
];

export default function TertiaryDashboard() {
  const [stats, setStats]         = useState({});
  const [enrolments, setEnrolments] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [graduations, setGraduations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterProg, setFilterProg] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/tertiary/stats'),
      api.get('/tertiary/enrolments', { params: { status:'active' } }),
      api.get('/tertiary/programmes'),
      api.get('/tertiary/graduations'),
    ]).then(([s, e, p, g]) => {
      setStats(s.data.data || {});
      setEnrolments(e.data.data || []);
      setProgrammes(p.data.data || []);
      setGraduations(g.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = filterProg
    ? enrolments.filter(e => String(e.programme_id) === String(filterProg))
    : enrolments;

  // GPA distribution
  const gpaDist = GPA_BANDS.map(band => ({
    ...band,
    count: filtered.filter(e => {
      const g = parseFloat(e.gpa);
      return g >= band.min && g <= band.max;
    }).length
  }));

  const total = filtered.length || 1;

  // Top performers
  const topPerformers = [...filtered]
    .filter(e => parseFloat(e.gpa) > 0)
    .sort((a,b) => parseFloat(b.gpa) - parseFloat(a.gpa))
    .slice(0, 10);

  // Programme breakdown
  const progBreakdown = programmes.map(p => ({
    ...p,
    students: enrolments.filter(e => e.programme_id === p.id),
    avgGpa: (() => {
      const s = enrolments.filter(e => e.programme_id === p.id && parseFloat(e.gpa) > 0);
      return s.length ? (s.reduce((sum,e) => sum + parseFloat(e.gpa), 0) / s.length).toFixed(3) : '0.000';
    })()
  }));

  if (loading) return <Box sx={{ p:3 }}><LinearProgress/></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Tertiary Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Academic performance overview</Typography>
        </Box>
        <TextField select size="small" label="Filter by Programme" value={filterProg}
          onChange={e=>setFilterProg(e.target.value)} sx={{ minWidth:220 }}>
          <MenuItem value="">All Programmes</MenuItem>
          {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
      </Stack>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label:'Programmes', value:stats.programmes||0, icon:<School/>, color:'#0f2b4a' },
          { label:'Enrolled Students', value:stats.enrolled||0, icon:<People/>, color:'#2e7d32' },
          { label:'Courses', value:stats.courses||0, icon:<MenuBook/>, color:'#1565c0' },
          { label:'Graduated', value:stats.graduated||0, icon:<EmojiEvents/>, color:'#c9952a' },
          { label:'Avg GPA', value:parseFloat(stats.avg_gpa||0).toFixed(2), icon:<School/>, color:'#7b1fa2' },
        ].map(s => (
          <Grid item xs={6} sm={4} md={2.4} key={s.label}>
            <Card variant="outlined" sx={{ borderLeft:`4px solid ${s.color}`, borderRadius:2 }}>
              <CardContent sx={{ pb:'12px !important', px:2 }}>
                <Typography variant="caption" color="text.secondary" fontSize={10} textTransform="uppercase" fontWeight={700}>{s.label}</Typography>
                <Typography variant="h4" fontWeight={800} sx={{ color:s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* GPA Distribution */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ borderRadius:2, overflow:'hidden' }}>
            <Box sx={{ bgcolor:'#0f2b4a', p:2 }}>
              <Typography fontWeight={700} color="white">GPA Distribution</Typography>
              <Typography variant="caption" sx={{ opacity:0.6, color:'white' }}>{filtered.length} active students</Typography>
            </Box>
            <Box sx={{ p:2 }}>
              {gpaDist.map(band => (
                <Box key={band.label} sx={{ mb:2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:0.5 }}>
                    <Box>
                      <Typography fontSize={13} fontWeight={600} sx={{ color:band.color }}>{band.desc}</Typography>
                      <Typography fontSize={11} color="text.secondary">{band.label}</Typography>
                    </Box>
                    <Typography fontWeight={800} sx={{ color:band.color }}>{band.count}</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={(band.count/total)*100}
                    sx={{ height:8, borderRadius:4, bgcolor:'#f0f0f0',
                      '& .MuiLinearProgress-bar': { bgcolor:band.color, borderRadius:4 } }}/>
                  <Typography fontSize={11} color="text.secondary" sx={{ mt:0.3 }}>
                    {((band.count/total)*100).toFixed(1)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Top Performers */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ borderRadius:2, overflow:'hidden' }}>
            <Box sx={{ bgcolor:'#c9952a', p:2 }}>
              <Typography fontWeight={700} color="white">Top Performers</Typography>
              <Typography variant="caption" sx={{ opacity:0.8, color:'white' }}>Students ranked by GPA</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor:'#f8f9fc' }}>
                  {['#','Student','Programme','Credits','GPA','Classification'].map(h => (
                    <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {topPerformers.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py:3, color:'text.secondary' }}>No graded students yet</TableCell></TableRow>
                  ) : topPerformers.map((s,i) => (
                    <TableRow key={s.id} hover sx={{ bgcolor: i < 3 ? 'rgba(201,149,42,0.05)' : 'inherit' }}>
                      <TableCell>
                        <Typography fontWeight={700} sx={{ color: i===0?'#c9952a':i===1?'#9e9e9e':i===2?'#795548':'text.secondary' }}>
                          {i+1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={600}>{s.last_name} {s.first_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{s.student_number}</Typography>
                      </TableCell>
                      <TableCell><Typography fontSize={12} color="text.secondary">{s.programme_code}</Typography></TableCell>
                      <TableCell><Chip label={s.total_credits_earned} size="small" color="info"/></TableCell>
                      <TableCell>
                        <Typography fontWeight={800} sx={{ color:gpaColor(s.gpa) }}>
                          {parseFloat(s.gpa).toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={getClassification(s.gpa)} size="small"
                          sx={{ bgcolor:gpaColor(s.gpa), color:'white', fontWeight:600, fontSize:10 }}/>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Programme Breakdown */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ borderRadius:2, overflow:'hidden' }}>
            <Box sx={{ bgcolor:'#2e7d32', p:2 }}>
              <Typography fontWeight={700} color="white">Programme Performance</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor:'#f8f9fc' }}>
                  {['Programme','Type','Duration','Students','Avg GPA','Pass Rate','Top Classification'].map(h => (
                    <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {progBreakdown.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py:3, color:'text.secondary' }}>No programmes yet</TableCell></TableRow>
                  ) : progBreakdown.map(p => {
                    const passing = p.students.filter(s => parseFloat(s.gpa) >= 2.0).length;
                    const passRate = p.students.length ? ((passing/p.students.length)*100).toFixed(0) : 0;
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Typography fontWeight={600} fontSize={13}>{p.name}</Typography>
                          <Typography fontSize={11} color="text.secondary">{p.code}</Typography>
                        </TableCell>
                        <TableCell><Chip label={p.type} size="small" color="primary"/></TableCell>
                        <TableCell>{p.duration_years} yrs</TableCell>
                        <TableCell><Chip label={p.students.length} size="small" color="success"/></TableCell>
                        <TableCell>
                          <Typography fontWeight={700} sx={{ color:gpaColor(p.avgGpa) }}>{p.avgGpa}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LinearProgress variant="determinate" value={parseFloat(passRate)}
                              sx={{ width:60, height:6, borderRadius:3, bgcolor:'#f0f0f0',
                                '& .MuiLinearProgress-bar':{ bgcolor: parseFloat(passRate)>=70?'#2e7d32':'#c9952a' }}}/>
                            <Typography fontSize={12}>{passRate}%</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip label={getClassification(p.avgGpa)} size="small"
                            sx={{ bgcolor:gpaColor(p.avgGpa), color:'white', fontSize:10 }}/>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
